import { Gift } from "lucide-react";
import { useOtherIncome } from "@/hooks/useOtherIncome";
import { categoryFor } from "@/lib/wallet/incomeCategories";

const fmt = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const SOURCE_LABEL: Record<string, string> = {
  parent_action: "Added by parent",
  admin_action: "Added by HeirWise",
  module_reward: "Earned from a module",
  test_seed: "Test deposit",
  household_allowance: "Household allowance",
};

export default function OtherIncomeCard() {
  const { loading, items, totalThisMonth } = useOtherIncome(10);

  if (loading) {
    return <div className="glass-card p-5 animate-pulse h-32" />;
  }

  return (
    <div className="glass-card p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Gift className="w-4 h-4 text-primary" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Other Income
            </span>
          </div>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground max-w-md">
            Money you earn outside of a paycheck shows up here — lesson and module rewards you
            earned. These are added directly to your Sim Wallet and do not use paycheck taxes.
          </p>

        </div>
        {items.length > 0 && (
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">This month</p>
            <p className="font-heading text-xl font-bold text-primary">{fmt(totalThisMonth)}</p>
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border/60 bg-muted/20 px-4 py-5 text-center">
          <p className="font-heading font-semibold text-sm text-foreground">No Other Income Yet</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Rewards you earn from lessons and modules will appear here once they land in your Sim
            Wallet.
          </p>

        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => {
            const meta = categoryFor(item.type);
            const Icon = meta.icon;
            const sourceLabel = item.source ? SOURCE_LABEL[item.source] ?? null : null;
            return (
              <li
                key={item.id}
                className="flex items-center gap-3 rounded-xl border border-border/40 bg-background/40 px-3 py-2"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                  <Icon className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">{meta.label}</p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {sourceLabel ?? item.description ?? "Deposit"} · {fmtDate(item.createdAt)}
                  </p>
                </div>
                <span className="font-heading text-sm font-bold text-primary">
                  +{fmt(item.amount)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}